/**
 * Authentication API Client
 *
 * Handles JWT authentication with the backend using wallet signatures.
 * Flow:
 * 1. Get signing message from backend
 * 2. User signs message with wallet
 * 3. Submit signature to backend for JWT token
 */

import config from '@/config';

const BASE_URL = config.API_BASE_URL;

/**
 * Custom error class that preserves HTTP status code
 */
export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export interface GetAuthMessageRequest {
  address: string;
}

export interface GetAuthMessageResponse {
  code: number;
  data: {
    message: string;
  };
  message?: string;
}

export interface SignInRequest {
  address: string;
  message: string;
  signature: string;
  account_type: 'SVM' | 'EVM';
  wallet_provider?: string;
}

export interface UserInfo {
  address: string;
  wallet_provider?: string;
  activeAddress?: string;
}

export interface SignInResponse {
  code: number;
  data: UserInfo;
  message?: string;
}

/**
 * Get authentication message to sign
 * @param address - Wallet address
 * @returns Message to sign with wallet
 */
export async function getAuthMessage(address: string): Promise<GetAuthMessageResponse> {
  const response = await fetch(`${BASE_URL}/auth/auth-message`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ address }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get auth message: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Sign in with wallet signature
 * Backend sets JWT token as HTTP-only cookie (no token in response body)
 * @param data - Sign in request data
 * @returns User info (token is set as cookie)
 */
export async function signIn(data: SignInRequest): Promise<SignInResponse> {
  const response = await fetch(`${BASE_URL}/auth/sign-in`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Required for cookies
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorBody = await response.json();
      if (typeof errorBody?.message === 'string') {
        errorMessage = errorBody.message;
      }
    } catch {
      try {
        const text = await response.text();
        if (text) {
          errorMessage = text;
        }
      } catch {
        // ignore
      }
    }
    throw new HttpError(errorMessage, response.status, response.statusText);
  }

  return response.json();
}

/**
 * Sign out (clears JWT cookie)
 */
export async function signOut(): Promise<void> {
  const response = await fetch(`${BASE_URL}/auth/sign-out`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Sign out failed: ${response.statusText}`);
  }
}

export interface GetUserInfoResponse {
  code: number;
  data: UserInfo;
  message?: string;
}

/**
 * Get authenticated user info
 * Uses JWT token from HTTP-only cookie
 */
export async function getUserInfo(): Promise<GetUserInfoResponse> {
  const response = await fetch(`${BASE_URL}/auth/user-info`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new HttpError(
      `Failed to get user info: ${response.statusText}`,
      response.status,
      response.statusText
    );
  }

  return response.json();
}

/**
 * Create authenticated fetch wrapper
 * Uses JWT token from HTTP-only cookie (no Authorization header needed)
 * @returns Fetch wrapper with credentials
 */
export function createAuthenticatedClient() {
  return async function authenticatedFetch(url: string, options: RequestInit = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Automatically includes cookie
    });

    return response;
  };
}

export interface BackpackCampaignInfoResponse {
  code: number;
  data: {
    isActive: boolean;
    startDate: string;
    endDate: string;
    maxBonus: number;
    multiplier: number;
  };
  message?: string;
}

export interface BackpackCampaignStatusResponse {
  code: number;
  data: {
    isActive: boolean;
    startDate: string;
    endDate: string;
    userBonusEarned: number;
    maxBonus: number;
    remainingBonus: number;
    isEligible: boolean;
    walletProvider: string | null;
  };
  message?: string;
}

export interface SolflareCampaignStatusResponse {
  code: number;
  data: {
    isActive: boolean;
    startDate: string;
    endDate: string;
    userBonusEarned: number;
    maxBonus: number;
    remainingBonus: number;
    isEligible: boolean;
    walletProvider: string | null;
  };
  message?: string;
}

/**
 * Get public Backpack campaign info (no authentication required)
 */
export async function getBackpackCampaignInfo(): Promise<BackpackCampaignInfoResponse> {
  const response = await fetch(`${BASE_URL}/auth/backpack-campaign/info`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to get campaign info: ${response.statusText}`);
  }

  return response.json();
}

async function fetchCampaignStatus<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to get campaign status: ${response.statusText}`);
  }

  return response.json();
}

export const getBackpackCampaignStatus = () =>
  fetchCampaignStatus<BackpackCampaignStatusResponse>(`${BASE_URL}/auth/backpack-campaign/status`);

export const getSolflareCampaignStatus = () =>
  fetchCampaignStatus<SolflareCampaignStatusResponse>(`${BASE_URL}/auth/solflare-campaign/status`);

export interface MagicEdenCampaignStatusResponse {
  code: number;
  data: {
    isActive: boolean;
    startDate: string;
    endDate: string;
    userBonusEarned: number;
    maxBonus: number;
    remainingBonus: number;
    isEligible: boolean;
    isMagicEdenStaker: boolean;
  };
  message?: string;
}

export const getMagicEdenCampaignStatus = () =>
  fetchCampaignStatus<MagicEdenCampaignStatusResponse>(`${BASE_URL}/auth/magic-eden-campaign/status`);

export interface ReferralCampaignStatusResponse {
  code: number;
  data: {
    isEligible: boolean;
    boundReferralCode: string | null;
    userBonusEarned: number;
    maxBonus: number;
    remainingBonus: number;
  };
  message?: string;
}

export const getReferralCampaignStatus = () =>
  fetchCampaignStatus<ReferralCampaignStatusResponse>(`${BASE_URL}/invite/referral-campaign/status`);

export interface GateCampaignStatusResponse {
  code: number;
  data: {
    isActive: boolean;
    startDate: string;
    endDate: string;
    userBonusEarned: number;
    maxBonus: number;
    remainingBonus: number;
    isEligible: boolean;
    walletProvider: string | null;
  };
  message?: string;
}

export const getGateCampaignStatus = () =>
  fetchCampaignStatus<GateCampaignStatusResponse>(`${BASE_URL}/auth/gate-campaign/status`);

export interface BreakpointWinnerCampaignStatusResponse {
  code: number;
  data: {
    isActive: boolean;
    startDate: string;
    endDate: string;
    userBonusEarned: number;
    maxBonus: number;
    remainingBonus: number;
    isEligible: boolean;
    isBreakpointWinner: boolean;
  };
  message?: string;
}

export const getBreakpointWinnerCampaignStatus = () =>
  fetchCampaignStatus<BreakpointWinnerCampaignStatusResponse>(`${BASE_URL}/auth/breakpoint-winner-campaign/status`);

export interface CflCampaignStatusResponse {
  code: number;
  data: {
    isActive: boolean;
    startDate: string;
    endDate: string;
    userBonusEarned: number;
    maxBonus: number;
    remainingBonus: number;
    isEligible: boolean;
    isCflWhitelisted: boolean;
  };
  message?: string;
}

export const getCflCampaignStatus = () =>
  fetchCampaignStatus<CflCampaignStatusResponse>(`${BASE_URL}/auth/cfl-campaign/status`);

export interface SmbMatricaCampaignStatusResponse {
  code: number;
  data: {
    isActive: boolean;
    startDate: string;
    endDate: string;
    userBonusEarned: number;
    maxBonus: number;
    remainingBonus: number;
    isEligible: boolean;
    isSmbMatricaEligible: boolean;
  };
  message?: string;
}

export const getSmbMatricaCampaignStatus = () =>
  fetchCampaignStatus<SmbMatricaCampaignStatusResponse>(`${BASE_URL}/auth/smb-matrica-campaign/status`);

export interface MattleCampaignStatusResponse {
  code: number;
  data: {
    isActive: boolean;
    startDate: string;
    endDate: string;
    userBonusEarned: number;
    maxBonus: number;
    remainingBonus: number;
    isEligible: boolean;
    isMattleWhitelisted: boolean;
  };
  message?: string;
}

export const getMattleCampaignStatus = () =>
  fetchCampaignStatus<MattleCampaignStatusResponse>(`${BASE_URL}/auth/mattle-campaign/status`);
